import SlInput from "@shoelace-style/shoelace/dist/components/input/input";
import { addStyles } from "../../helpers/addStyles";
import { pad } from "@tedivo/tedivo-pure-helpers";

export class InputPadZeros extends HTMLElement {
  public static formAssociated = true;
  public static observedAttributes = ["class", "disabled"];

  public required = false;
  public size: "small" | "medium" | "large" = "medium";

  private internalValue? = 0;
  public padZeros = 0;
  private internalId = "";
  private input: SlInput;
  private internalLabel = "";
  private internalDisabled = false;
  private internalUseInputListener = false;

  constructor() {
    super();

    const inp = document.createElement("sl-input");
    inp.noSpinButtons = true;
    this.input = inp;
  }

  get value(): number | undefined {
    this.calculateInternalValue();
    return this.internalValue;
  }

  set value(v: number | undefined) {
    this.internalValue = v;
    this.setDisplayValue(v);
  }

  get label() {
    return this.internalLabel;
  }

  set label(v: string) {
    this.internalLabel = v;
    if (this.input) this.input.label = v;
  }

  set helpText(v: string) {
    if (this.input) this.input.helpText = v;
  }

  get name() {
    return this.internalId;
  }

  set name(v: string) {
    this.internalId = v;
    if (this.input) {
      this.input.id = v;
      this.input.name = v;
    }
  }

  get disabled() {
    return this.internalDisabled;
  }
  set disabled(s: boolean) {
    this.internalDisabled = s;
    if (this.input) this.input.disabled = s;
  }

  get min() {
    return Number(this.input?.min) || undefined;
  }
  set min(s: number | undefined) {
    if (this.input) this.input.min = s ?? 0;
  }

  get max() {
    return Number(this.input?.max) || undefined;
  }
  set max(s: number | undefined) {
    if (this.input) this.input.max = s ?? 0;
  }

  override get tabIndex(): number {
    return this.input?.tabIndex || -1;
  }

  override set tabIndex(v: number) {
    if (this.input) this.input.tabIndex = v;
  }

  get noSpinButtons() {
    if (this.input) return this.input.noSpinButtons;
    return false;
  }

  set noSpinButtons(v: boolean) {
    if (this.input) this.input.noSpinButtons = v;
  }

  override focus() {
    if (this.input) this.input.focus();
  }

  select() {
    if (this.input) this.input.select();
  }

  private setDisplayValue(
    v: number | undefined,
    overrideValue?: string,
  ): Promise<void> {
    return new Promise((resolve) => {
      if (!this.input) {
        resolve();
        return;
      }

      if (overrideValue) {
        this.input.value = overrideValue;
        resolve();
        return;
      }

      if (v === undefined || isNaN(v)) {
        this.input.value = "";
        resolve();
        return;
      }

      this.input.value = pad(v, this.padZeros || 0);
      resolve();
    });
  }

  connectedCallback() {
    const shadow = this.attachShadow({ mode: "open" });

    const inp = this.input;
    inp.id = this.name;
    inp.name = this.name;
    inp.size = this.size;
    inp.type = "number";

    if (this.required) inp.required = true;
    if (this.label) inp.label = this.label;
    if (this.disabled) inp.disabled = this.disabled;

    shadow.appendChild(addStyles(styles));
    shadow.appendChild(inp);

    inp.addEventListener("sl-change", this.onChange, false);
    inp.addEventListener("focus", this._onFocus, false);
    inp.addEventListener("blur", this._onBlur, false);
  }

  private _onFocus = () => {
    this.internalValue = Number(this.internalValue);
    this.setDisplayValue(
      this.internalValue,
      this.internalValue?.toString(),
    ).then(() => {
      if (this.input) this.input.select();
    });
  };

  private _onBlur = () => {
    this.setDisplayValue(this.internalValue);
  };

  disconnectedCallback() {
    this.input.removeEventListener("sl-change", this.onChange, false);
    this.input.removeEventListener("focus", this._onFocus, false);
    this.input.removeEventListener("sl-blur", this._onBlur, false);
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    switch (name) {
      case "class":
        if (newValue.indexOf("has-error") >= 0) {
          if (!this.input.classList.contains("has-error"))
            this.input.classList.add("has-error");
        } else {
          this.input.classList.remove("has-error");
        }
        break;
      case "disabled":
        this.disabled = newValue === "true";
        break;
    }
  }

  private onChange = () => {
    this.calculateInternalValue();
  };

  private calculateInternalValue = () => {
    const t = this.input;
    if (t.value === "") {
      this.internalValue = undefined;
      return;
    }

    const faceValue = Number(t.value);

    this.internalValue = faceValue;
  };
}

customElements.define("tf-input-padded", InputPadZeros);
declare global {
  interface HTMLElementTagNameMap {
    "tf-input-padded": InputPadZeros;
  }
}

const styles = `
 
  sl-input.has-error:not([disabled])::part(form-control-label),
  sl-input.has-error:not([disabled])::part(form-control-help-text) {
    color: var(--sl-color-danger-700);
  }

  sl-input.has-error:not([disabled])::part(base) {
    border-color: var(--sl-color-danger-600);
  }

  sl-input.has-error:focus-within::part(base) {
    box-shadow: 0 0 0 var(--sl-focus-ring-width) var(--sl-color-danger-200);
  }
  
`;
